import { Button } from 'components/Button';
import { Card } from 'components/Card';
import {} from 'components/CardGeneric';
import { CardRow } from 'components/CardRow';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { useAlert } from 'hooks/useAlert';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { str } from 'lib/str';
import { cloneDeep, intersection, isEqual, union, without } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  REMOTE_ASESETUP_CHECKLISTITEMCLOSED,
  REMOTE_ASESETUP_ACTIONDELETE,
  REMOTE_ASESETUP_CHECKLISTITEMDELETE,
  REMOTE_ASESETUP_CONTROLDELETE,
  REMOTE_ASESETUP_ACTIONOPEN,
  REMOTE_ASESETUP_CONTROLOPEN,
  REMOTE_ASESETUP_CHECKLISTITEMSAVE,
  SET_INCOMING_VALUE,
} from 'store/actions';
import { selectIncomingValue } from 'store/app/selectors';
import {
  selectAseSetupChecklistItemHasActions,
  selectAseSetupChecklistItemHasControls,
  selectAseSetupChecklistItemId,
  selectAseSetupLibraryChecklistItem,
  selectAseSetupLibraryControlIdsAlphaOrdered,
  selectAseSetupLibraryActionIdsAlphaOrdered,
} from 'store/remote/selectors';
import { AssessmentLibrary } from 'store/remote/types';
import { FindingRow } from './Rows/FindingRow';
import { PageHeader } from 'components/PageHeader';

export const ChecklistItemScreen: React.FC<
  ModalScreenProps<ModalScreens.ASESETUPLIBRARYCHECKLISTITEM>
> = ({ route, navigation }) => {
  const dispatch = useDispatch();
  const forceUpdate = useForceUpdate();
  const alert = useAlert();
  const confirmUnsaved = useConfirmUnsaved();

  const linkActionsSelectionKey = 'AseSetupChecklistItemLinkActions';
  const linkControlsSelectionKey = 'AseSetupChecklistItemLinkControls';
  const addActionSelectionKey = 'AseSetupChecklistItemAddAction';
  const addControlSelectionKey = 'AseSetupChecklistItemAddControl';

  const { questionId: inQuestionId } = route.params;

  const linkActionIds = useSelector((state) =>
    selectIncomingValue(state, linkActionsSelectionKey),
  ) as unknown as number[] | undefined;

  const linkControlIds = useSelector((state) =>
    selectIncomingValue(state, linkControlsSelectionKey),
  ) as unknown as number[] | undefined;

  const addActionId = useSelector((state) =>
    selectIncomingValue(state, addActionSelectionKey),
  ) as unknown as number | undefined;

  const addControlId = useSelector((state) =>
    selectIncomingValue(state, addControlSelectionKey),
  ) as unknown as number | undefined;

  const checklistItemId = useSelector(selectAseSetupChecklistItemId);
  const initIdRef = React.useRef(checklistItemId);

  const checklistItem: AssessmentLibrary['checklistItems'][number] =
    useSelector((state) =>
      selectAseSetupLibraryChecklistItem(state, checklistItemId || -1),
    ) || {
      id: 0,
      questionId: inQuestionId!,
      text: '',
      ordering: 0,
    };

  // We need all action IDs so we can order them correctly
  const libraryActionIds = useSelector((state) =>
    selectAseSetupLibraryActionIdsAlphaOrdered(state, '_'),
  );
  const libraryControlIds = useSelector((state) =>
    selectAseSetupLibraryControlIdsAlphaOrdered(state, '_'),
  );

  const actions = useSelector((state) =>
    selectAseSetupChecklistItemHasActions(state, checklistItem.id),
  );
  const controls = useSelector((state) =>
    selectAseSetupChecklistItemHasControls(state, checklistItem.id),
  );

  const afterCreate = React.useRef<
    undefined | 'addaction' | 'linkactions' | 'addcontrol' | 'linkcontrols'
  >(undefined);

  const isDirty = React.useRef(false);
  const data = React.useRef(cloneDeep(checklistItem));

  const actionIds = React.useRef<{ positive: number[]; negative: number[] }>({
    positive: actions.positive,
    negative: actions.negative,
  });
  // Ensure there aren't deleted actions in the list
  actionIds.current.positive = intersection(
    actionIds.current.positive,
    libraryActionIds,
  );
  actionIds.current.negative = intersection(
    actionIds.current.negative,
    libraryActionIds,
  );
  const allActionIds = union(
    actionIds.current.positive,
    actionIds.current.negative,
  );

  const controlIds = React.useRef<{ positive: number[]; negative: number[] }>({
    positive: controls.positive,
    negative: controls.negative,
  });
  // Ensure there aren't deleted controls in the list
  controlIds.current.positive = intersection(
    controlIds.current.positive,
    libraryControlIds,
  );
  controlIds.current.negative = intersection(
    controlIds.current.negative,
    libraryControlIds,
  );
  const allControlIds = union(
    controlIds.current.positive,
    controlIds.current.negative,
  );

  // Reset data if ID changes, then check isDirty
  if (data.current.id !== checklistItem.id) {
    data.current = cloneDeep(checklistItem);
  }

  const checkIsDirty = () =>
    !isEqual(checklistItem, data.current) ||
    !isEqual(actionIds.current.negative, actions.negative) ||
    !isEqual(actionIds.current.positive, actions.positive) ||
    !isEqual(controlIds.current.negative, controls.negative) ||
    !isEqual(controlIds.current.positive, controls.positive);

  isDirty.current = checkIsDirty();

  const shouldReload = () => {
    if (isDirty.current !== checkIsDirty()) {
      forceUpdate();
    }
  };

  const handleDeleteAction = (actionId: number) =>
    dispatch(REMOTE_ASESETUP_ACTIONDELETE({ id: actionId }));

  const handleUnlinkAction = (actionId: number) => {
    actionIds.current.negative = without(actionIds.current.negative, actionId);
    actionIds.current.positive = without(actionIds.current.positive, actionId);
    forceUpdate();
  };

  const handleDeleteControl = (controlId: number) =>
    dispatch(REMOTE_ASESETUP_CONTROLDELETE({ id: controlId }));

  const handleUnlinkControl = (controlId: number) => {
    controlIds.current.negative = without(
      controlIds.current.negative,
      controlId,
    );
    controlIds.current.positive = without(
      controlIds.current.positive,
      controlId,
    );
    forceUpdate();
  };

  const handleChangeAction = (
    actionId: number,
    type: 'positive' | 'negative' | 'both',
  ) => {
    actionIds.current.negative =
      type !== 'positive'
        ? union(actionIds.current.negative, [actionId])
        : without(actionIds.current.negative, actionId);
    actionIds.current.positive =
      type !== 'negative'
        ? union(actionIds.current.positive, [actionId])
        : without(actionIds.current.positive, actionId);
    forceUpdate();
  };

  const handleChangeControl = (
    control: number,
    type: 'positive' | 'negative' | 'both',
  ) => {
    controlIds.current.negative =
      type !== 'positive'
        ? union(controlIds.current.negative, [control])
        : without(controlIds.current.negative, control);
    controlIds.current.positive =
      type !== 'negative'
        ? union(controlIds.current.positive, [control])
        : without(controlIds.current.positive, control);
    forceUpdate();
  };

  const handleChangeWithName = <T extends keyof typeof data.current>(
    name: T,
    value: (typeof data.current)[T],
  ) => {
    data.current = {
      ...data.current,
      [name]: value,
    };
    shouldReload();
  };

  const handleClose = () => {
    if (isDirty.current) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  };

  const handleSave = (close: boolean = false) => {
    if (!checklistItem.id && data.current.text.trim() === '') {
      alert({
        text: 'Please enter text for your checklist item',
      });
      return;
    }
    dispatch(
      REMOTE_ASESETUP_CHECKLISTITEMSAVE({
        id: checklistItemId,
        data: {
          ...data.current,
          positiveActionIds: actionIds.current.positive,
          negativeActionIds: actionIds.current.negative,
          positiveControlIds: controlIds.current.positive,
          negativeControlIds: controlIds.current.negative,
        },
        addData: {
          questionId: checklistItem.questionId,
        },
        close,
      }),
    );
  };

  const handleSavePress = () => {
    if (isDirty.current || !checklistItem.id) {
      handleSave(true);
    } else {
      dispatch(NAVIGATE_MODAL_BACK());
    }
  };

  const handleOpenAction = React.useCallback(
    (id?: number) =>
      dispatch(
        REMOTE_ASESETUP_ACTIONOPEN({
          id,
          incomingValueNewIdKey: addActionSelectionKey,
          allowLinkManagement: false,
        }),
      ),
    [dispatch],
  );

  const handleOpenControl = React.useCallback(
    (id?: number) =>
      dispatch(
        REMOTE_ASESETUP_CONTROLOPEN({
          id,
          incomingValueNewIdKey: addControlSelectionKey,
          allowLinkManagement: false,
        }),
      ),
    [dispatch],
  );

  const doAfterCreate = React.useCallback(() => {
    switch (afterCreate.current) {
      case 'addaction':
        handleOpenAction();
        break;
      case 'linkactions':
        dispatch(
          NAVIGATE_MODAL({
            screen: ModalScreens.ASESETUPLIBRARYACTIONSLIST,
            params: {
              incomingValueKey: linkActionsSelectionKey,
              hideIds: union(
                actionIds.current.positive,
                actionIds.current.negative,
              ),
            },
          }),
        );
        break;
      case 'addcontrol':
        handleOpenControl();
        break;
      case 'linkcontrols':
        dispatch(
          NAVIGATE_MODAL({
            screen: ModalScreens.ASESETUPLIBRARYCONTROLSLIST,
            params: {
              incomingValueKey: linkControlsSelectionKey,
              hideIds: union(
                controlIds.current.positive,
                controlIds.current.negative,
              ),
            },
          }),
        );
        break;
      default:
    }
  }, [dispatch, handleOpenAction, handleOpenControl]);

  const preCheckBeforeAddFinding = (
    whatAfterCreate: typeof afterCreate.current,
  ) => {
    afterCreate.current = whatAfterCreate;
    if (!checklistItem.id) {
      handleSave();
      return;
    }
    doAfterCreate();
  };

  const handleAddAction = (link: boolean = false) =>
    preCheckBeforeAddFinding(link ? 'linkactions' : 'addaction');

  const handleAddControl = (link: boolean = false) =>
    preCheckBeforeAddFinding(link ? 'linkcontrols' : 'addcontrol');

  const handleDelete = () =>
    dispatch(
      REMOTE_ASESETUP_CHECKLISTITEMDELETE({
        id: checklistItem.id,
      }),
    );

  React.useEffect(() => {
    if (linkActionIds?.length) {
      actionIds.current.negative = union(
        actionIds.current.negative,
        linkActionIds,
      );
      dispatch(SET_INCOMING_VALUE({ key: linkActionsSelectionKey }));
    }
  }, [dispatch, linkActionIds]);

  React.useEffect(() => {
    if (linkControlIds?.length) {
      controlIds.current.positive = union(
        controlIds.current.positive,
        linkControlIds,
      );
      dispatch(SET_INCOMING_VALUE({ key: linkControlsSelectionKey }));
    }
  }, [dispatch, linkControlIds]);

  React.useEffect(() => {
    if (addActionId) {
      actionIds.current.negative = union(actionIds.current.negative, [
        addActionId,
      ]);
      dispatch(SET_INCOMING_VALUE({ key: addActionSelectionKey }));
    }
  }, [dispatch, addActionId]);

  React.useEffect(() => {
    if (addControlId) {
      controlIds.current.positive = union(controlIds.current.positive, [
        addControlId,
      ]);
      dispatch(SET_INCOMING_VALUE({ key: addControlSelectionKey }));
    }
  }, [dispatch, addControlId]);

  React.useEffect(() => {
    if (initIdRef.current && !checklistItemId) {
      dispatch(NAVIGATE_MODAL_BACK());
    } else if (initIdRef.current !== checklistItemId) {
      // Just got created
      initIdRef.current = checklistItemId;
      doAfterCreate();
    }
  }, [checklistItemId, dispatch, doAfterCreate]);

  React.useEffect(() => {
    return () => {
      dispatch(REMOTE_ASESETUP_CHECKLISTITEMCLOSED());
    };
  }, [dispatch]);

  const isDirtyVar = isDirty.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title={checklistItemId ? 'Checklist Item' : 'New Checklist Item'}
          onPressCancelButton={handleClose}
          onPressSaveButton={
            checklistItemId || isDirty.current ? handleSavePress : null
          }
        />
      }
    >
      <Card
        label="Checklist Item Text"
        explanation={str.checklistItemSetupTitleFieldExample}
      >
        <TextAreaField
          name="text"
          onChangeWithName={handleChangeWithName}
          value={data.current.text}
          onEsc={handleClose}
          autoFocusWeb
          autoFocusNative={!data.current.id}
          autoFocusDelay
        />
      </Card>

      <Card
        label="Actions"
        labelLinkText="New"
        onLabelLinkPress={() => handleAddAction(false)}
        labelLinkText2="Link"
        onLabelLinkPress2={() => handleAddAction(true)}
      >
        {allActionIds.length ? (
          libraryActionIds
            .filter((actionId) => allActionIds.includes(actionId))
            .map((actionId) => (
              <FindingRow
                id={actionId}
                key={actionId}
                type="action"
                forChecklistId={checklistItemId || -1}
                forAnswerId={0}
                forQuestionId={0}
                onPress={() => handleOpenAction(actionId)}
                inPositive={actionIds.current.positive.includes(actionId)}
                inNegative={actionIds.current.negative.includes(actionId)}
                onDelete={() => handleDeleteAction(actionId)}
                onUnlink={() => handleUnlinkAction(actionId)}
                onPressNegative={() => handleChangeAction(actionId, 'negative')}
                onPressPositive={() => handleChangeAction(actionId, 'positive')}
                onPressPositiveNegative={() =>
                  handleChangeAction(actionId, 'both')
                }
              />
            ))
        ) : (
          <CardRow value="No actions added yet" />
        )}
      </Card>

      <Card
        label="Controls"
        labelLinkText="New"
        onLabelLinkPress={() => handleAddControl(false)}
        labelLinkText2="Link"
        onLabelLinkPress2={() => handleAddControl(true)}
      >
        {allControlIds.length ? (
          libraryControlIds
            .filter((controlId) => allControlIds.includes(controlId))
            .map((controlId) => (
              <FindingRow
                id={controlId}
                key={controlId}
                type="control"
                forChecklistId={checklistItemId || -1}
                forAnswerId={0}
                forQuestionId={0}
                inPositive={controlIds.current.positive.includes(controlId)}
                inNegative={controlIds.current.negative.includes(controlId)}
                onPress={() => handleOpenControl(controlId)}
                onDelete={() => handleDeleteControl(controlId)}
                onUnlink={() => handleUnlinkControl(controlId)}
                onPressNegative={() =>
                  handleChangeControl(controlId, 'negative')
                }
                onPressPositive={() =>
                  handleChangeControl(controlId, 'positive')
                }
                onPressPositiveNegative={() =>
                  handleChangeControl(controlId, 'both')
                }
              />
            ))
        ) : (
          <CardRow value="No controls added yet" />
        )}
      </Card>
      {!!checklistItem.id && (
        <Button
          text="Delete Checklist Item"
          onPress={handleDelete}
          destructive
          style={{ alignSelf: 'center' }}
        />
      )}
    </Page>
  );
};
